import { useState, useEffect, useCallback } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Loader2, Palette, Save } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FIELDS = [
  { key: "primary_button_bg", label: "Primary button bg" },
  { key: "primary_button_color", label: "Primary button text" },
  { key: "primary_button_hover_bg", label: "Primary button hover" },
  { key: "accent_color", label: "Accent" },
  { key: "background", label: "Background" },
  { key: "muted", label: "Muted" },
];

export default function AdminDesignTokensPanel() {
  const [tokens, setTokens] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const auth = useCallback(() => ({ Authorization: `Bearer ${localStorage.getItem("session_token")}` }), []);

  useEffect(() => {
    fetch(`${API}/admin/design-tokens`, { headers: auth() })
      .then(r => r.ok ? r.json() : null)
      .then(d => setTokens(d))
      .finally(() => setLoading(false));
  }, [auth]);

  const save = async () => {
    setSaving(true);
    try {
      const r = await fetch(`${API}/admin/design-tokens`, {
        method: "PUT",
        headers: { ...auth(), "Content-Type": "application/json" },
        body: JSON.stringify(tokens)
      });
      if (r.ok) toast.success("Design tokens saved");
      else toast.error("Save failed");
    } finally { setSaving(false); }
  };

  if (loading) return <div className="py-6 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-gray-400" /></div>;
  if (!tokens) return null;

  return (
    <Card data-testid="admin-design-tokens-panel">
      <CardContent className="p-5">
        <h3 className="text-base font-bold flex items-center gap-2 mb-1"><Palette className="w-4 h-4" /> Design tokens</h3>
        <p className="text-xs text-gray-500 mb-4">Global UI tokens. Read by every component that consumes admin theming.</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {FIELDS.map(f => (
            <div key={f.key} className="flex items-center gap-3">
              <label className="text-xs font-medium text-gray-600 w-44 flex-shrink-0">{f.label}</label>
              <div className="flex items-center gap-2 flex-1">
                <input
                  type="color"
                  value={tokens[f.key] || "#000000"}
                  onChange={(e) => setTokens({ ...tokens, [f.key]: e.target.value })}
                  className="w-9 h-9 rounded border border-gray-200 cursor-pointer"
                  data-testid={`design-token-color-${f.key}`}
                />
                <Input
                  value={tokens[f.key] || ""}
                  onChange={(e) => setTokens({ ...tokens, [f.key]: e.target.value })}
                  className="font-mono text-xs h-9"
                  data-testid={`design-token-input-${f.key}`}
                />
              </div>
            </div>
          ))}
        </div>
        <Button
          onClick={save}
          disabled={saving}
          className="mt-4 bg-gray-900 hover:bg-gray-800 text-white"
          data-testid="design-tokens-save"
        >
          {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
          Save tokens
        </Button>
      </CardContent>
    </Card>
  );
}
